<template>
  <div class="product-card-parent q-p-1 q-p-sm-2" :class="computedClass" v-if="component" v-html="generatedHtml"
       :data-id="id"></div>
  <div v-else class="product-element q-p-1 q-p-sm-2" :class="computedClass" :data-id="id">
    <div
        class="product-element__container q-card q-h-100 q-p-3 q-shadow-sm q-rounded-3 q-position-relative q-bg-white q-overflow-hidden">
      <span v-if="discount"
            class="product-element__discount q-text-center q-text-white q-position-absolute q-discount q-p-1 q-bg-danger">
        <span class="q-discount-minus">-</span><span class="q-discount-discount">{{ discount }}</span>
        <span class="q-percentage">%</span>
      </span>
      <div class="product-element__image-container q-card-image">
        <img class="product-element__image q-mx-auto q-d-block q-card-img-top q-img-fluid " v-lazy="staticImage"
             :alt="title"/>
      </div>
      <div class="product-element__info-box q-card-body">
        <h6 class="product-element__title  q-mb-3 q-lh-base">{{ title }}</h6>
        <AutocompletePriceBox v-if="!useStylisationUrl" :product="firstVariant"/>
      </div>
      <a class="product-element__link q-stretched-link" :href="actualLink" @click="handleClick"
         :target="useStylisationUrl ? '_blank' : '_self'"></a>
    </div>
  </div>
</template>

<script setup lang="ts">
import AutocompletePriceBox from '@/components/AutocompletePopup/AutocompletePriceBox.vue'
import {computed, defineProps, inject, nextTick, onMounted, onUpdated, ref, toRefs, watch} from 'vue';

import {addDisplayMacros, formatPrice, generateStaticImage, getDiscount, getRealPrice, parsePrice} from '@/core/utils';
import {createTemplate} from "@/core/Template";
import {SmartSearchConfigInterface} from '@/core/types';

// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
const SmartSearchConfig = ref(inject<SmartSearchConfigInterface>('SmartSearchConfig')!);

const {smallDevice, mediumDevice, largeDevice, extraLargeDevice} = SmartSearchConfig.value.serp.gridSettings.column;
const useStylisationUrl = !!SmartSearchConfig.value.appConfiguration.stylizationUrl;

const computedClass = computed(() => {
  return [
    `q-g-col-sm-${12 / smallDevice}`,
    `q-g-col-md-${12 / mediumDevice}`,
    `q-g-col-lg-${12 / largeDevice}`,
    `q-g-col-xl-${12 / extraLargeDevice}`,
  ];
});

const {currency: currencyCode} = SmartSearchConfig.value.appConfiguration;

const props = defineProps(['product', 'component']);
const {id, link, title, link_click, variants} = toRefs(props.product);

const firstVariant = ref(variants.value[0]);
const secondPrice = firstVariant.value.old_price || firstVariant.value.sale_price;

const {price, oldPrice} = getRealPrice(parsePrice(firstVariant.value.price), parsePrice(secondPrice));
const omnibusPrice = parsePrice(firstVariant.value.omnibus_price);

const {width, height, useQuarticImages} = SmartSearchConfig.value.serp.productImageSize;
const {removeDecimals} = SmartSearchConfig.value.appConfiguration;

const staticImage = computed(() => {
  const imageLink = firstVariant.value.image_link
  if (!useQuarticImages) {
    return imageLink
  }
  return imageLink ? generateStaticImage(imageLink, width, height) : generateStaticImage('https://static.quartic.com.pl/img/no-image/no-image.png', width, height);
})

const formattedPrice = ref(formatPrice(price, currencyCode, removeDecimals));
const formattedPriceOld = ref(formatPrice(oldPrice, currencyCode, removeDecimals));
const formattedOmnibusPrice = ref(formatPrice(omnibusPrice, currencyCode, removeDecimals));
const discount = ref(getDiscount(price, oldPrice));

const actualLink = ref(link.value);

const repairLink = (link: string) => link.replace(/^http:/, 'https:');

const handleClick = () => {
  if (useStylisationUrl) {
    actualLink.value = `${SmartSearchConfig.value.appConfiguration.stylizationUrl}/${id.value}`;
    return;
  }
  actualLink.value = repairLink(link_click.value);
};

const prepareClearLink = (link: string, clickLink: string) => {
  return ` href="${link}" data-href="${repairLink(clickLink)}" onmousedown="return this.href = this.getAttribute('data-href');" ontouchstart="return this.href = this.getAttribute('data-href');" `;
};

let generatedHtml = '';
if (props.component) {
  firstVariant.value.discount = discount.value;
  firstVariant.value.image_link_cdn = staticImage.value;
  firstVariant.value.price_formatted = formattedPrice.value;
  firstVariant.value.price_number = price;
  firstVariant.value.old_price_formatted = formattedPriceOld.value;
  firstVariant.value.old_price_number = oldPrice;
  firstVariant.value.omnibus_price_formatted = formattedOmnibusPrice.value;
  firstVariant.value.omnibus_price_number = omnibusPrice;
  firstVariant.value.id = id.value;
  firstVariant.value.link_click = link_click.value;
  firstVariant.value.clear_link = prepareClearLink(link.value, link_click.value);
  addDisplayMacros(firstVariant.value);
  generatedHtml = createTemplate(props.component, firstVariant.value, variants.value);
}


onUpdated(() => {
  if (!props.component) return;

  const container = document.querySelector('.product-card-parent');
  if (!container) return;

  if (window._QONizer?.onProductMounted) {
    try {
      const plainProduct = JSON.parse(JSON.stringify(props.product));
      window._QONizer.onProductMounted(plainProduct);
    } catch (e) {
      console.warn('Błąd przy przetwarzaniu props.product dla _QONizer:', e);
    }
  }
  if (window._QONizer?.customSmartSearchConfig?.onProductMounted) {
    try {
      const plainProduct = JSON.parse(JSON.stringify(props.product));
      window._QONizer?.customSmartSearchConfig?.onProductMounted(plainProduct);
    } catch (e) {
      console.warn('Błąd przy przetwarzaniu props.product dla _QONizer:', e);
    }
  }

});


function fireQonizer() {
  if (!props.component) return;

  nextTick(() => {
    const el = document.querySelector(
        `.product-card-parent[data-id="${id.value}"]`
    );
    if (!el) return;
    if (window._QONizer?.onProductMounted) {
      try {
        window._QONizer?.onProductMounted?.(JSON.parse(JSON.stringify(props.product)));
      } catch (e) {
        console.warn('onProductMounted error:', e);
      }
    }

    if (window._QONizer?.customSmartSearchConfig?.onProductMounted) {
      try {
        const plainProduct = JSON.parse(JSON.stringify(props.product));
        window._QONizer?.customSmartSearchConfig?.onProductMounted(plainProduct);
      } catch (e) {
        console.warn('Błąd przy przetwarzaniu props.product dla _QONizer:', e);
      }
    }
  });
}

onMounted(fireQonizer);

watch(
    () => [generatedHtml, props.product, props.component],
    fireQonizer,
    {deep: true, immediate: false}
);


</script>

<style scoped>
.product-card-parent > div {
  min-height: 100% !important;
}

.product-element__discount {
  font-size: 0.75rem;
}

.product-element__title {
  font-size: 0.75rem;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

.product-element__container {
  transition: height 0.15s ease-in-out, width 0.15s ease-in-out, color 0.15s ease-in-out, background-color 0.15s ease-in-out, border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}

.product-element__container:hover {
  box-shadow: var(--q-box-shadow) !important;
}

.q-discount {
  background-color: #e5002b;
  border-radius: 5px;
  right: 1rem;
}

.q-card {
  border: none;
}

img {
  height: auto;
  width: 100%;
  margin: auto;
  display: block;
}

img[lazy=loaded] {
  animation-duration: 400ms;
  animation-fill-mode: both;
  animation-name: fadeIn;
}

img.q-lazy[data-src] {
  filter: blur(10px);
  transition: filter 0.3s;
}

</style>
